import {defineStore} from "pinia";
import {useHTTP} from "@/plugins/http";
import {ref} from "vue";
import {useContext} from "@/plugins/context";
import {getApiUrl} from "@/utils/apiUrl";

export interface SettingSectionInterface {
    id: string;
    enName: string | null;
    arName: string | null;
    key?: string | null;
    icon?: string | null;
    settings: Array<SettingsInterface>;
    isPrivate: boolean;
}

export interface SettingsInterface {
    id: string;
    sectionID?: string;
    enName: string | null;
    arName: string | null;
    key: string;
    type?: string;
    options?: any;
    value?: string | null;
    isPrivate: boolean;
    settingGroup?: string | null;
}

export const useSettingsStore = defineStore("settingsStore", () => {
    const http = useHTTP()
    const ctx = useContext()
    const baseUrl = ref(getApiUrl());
    const storagePath = ref(new URL(baseUrl.value).origin + '/static');

    const sections = ref<Array<SettingSectionInterface>>([])
    const settings = ref<Array<SettingsInterface>>([])
    const showPrivateSettings = ref(false)
    const cities = ref([
        { enLabel: "Baghdad", arLabel: "بغداد", value: "Baghdad" },
        { enLabel: "Basra", arLabel: "البصرة", value: "Basra" },
        { enLabel: "Ninawa", arLabel: "نينوى", value: "Ninawa" },
        { enLabel: "Erbil", arLabel: "اربيل", value: "Erbil" },
        { enLabel: "Al-Anbar", arLabel: "الانبار", value: "Al-Anbar" },
        { enLabel: "Salah Al-Din", arLabel: "صلاح الدين", value: "Salah Al-Din" },
        { enLabel: "Kirkuk", arLabel: "كركوك", value: "Kirkuk" },
        { enLabel: "Najaf", arLabel: "النجف", value: "Najaf" },
        { enLabel: "Karbala", arLabel: "كربلاء", value: "Karbala" },
        { enLabel: "Sulaymaniyah", arLabel: "سليمانية", value: "Sulaymaniyah" },
        { enLabel: "Dhi Qar", arLabel: "ذي قار", value: "Dhi Qar" },
        { enLabel: "Babil", arLabel: "بابل", value: "Babil" },
        { enLabel: "Al-Qadisiyyah", arLabel: "القادسية", value: "Al-Qadisiyyah" },
        { enLabel: "Diyala", arLabel: "ديالى", value: "Diyala" },
        { enLabel: "Duhok", arLabel: "دهوك", value: "Duhok" },
        { enLabel: "Maysan", arLabel: "ميسان", value: "Maysan" },
        { enLabel: "Muthanna", arLabel: "المثنى", value: "Muthanna" },
        { enLabel: "Wasit", arLabel: "واسط", value: "Wasit" },
        { enLabel: "Halabja", arLabel: "حلبجة", value: "Halabja" }
    ]);



    async function loadStoragePath() {
        try {
            const response = await http.get<{ baseURL: string }>("/storage/base-url");
            if (response && response.baseURL) {
                storagePath.value = response.baseURL;
            } else {
                // Fallback to default
                storagePath.value = 'http://127.0.0.1:3001/static';
            }
        } catch (error) {
            // Fallback to default on error
            console.warn('Failed to load storage base URL, using default:', error);
            storagePath.value = 'http://127.0.0.1:3001/static';
        }
    }

    async function loadSettings(force_reload = false) {
        if (!settings.value.length || force_reload) {
            // Load storage path first
            await loadStoragePath();

            sections.value = await http.get<Array<SettingSectionInterface>>("/settings/settings");
            sections.value.forEach(section => {
                if (section.settings) {
                    section.settings.forEach(setting => {
                        settings.value.push(setting)
                        legacyLoad(setting)
                    })
                }
            })
        }
    }

    function legacyLoad(setting: SettingsInterface) {
        const ctx = useContext()
        switch (setting.key) {
            case 'system_language':
                ctx.setLocale(setting.value!)
                break;
            case 'receipt_language':
                ctx.setReceiptLanguage(setting.value!)
                break;
            case 'receipt_size':
                ctx.receiptSize = setting.value!
                break;
            case 'account_statement_receipt_size':
                ctx.accountStatementSize = setting.value!
                break;
            case 'tx_expense_receipt_size':
                ctx.transactionExpenseSize = setting.value!
                break;
            case 'sale_rep_required':
                // ctx.isSalesRepRequired = setting.value == 'true'
                break;
            case 'usd_to_iqd_rate':
                ctx.exchangeRates.IQD = parseFloat(setting.value!)
                break;
            case 'iqd_to_usd_rate':
                ctx.exchangeRates.USD = parseFloat(setting.value!)
                break;
            case 'machine_timezone':
                ctx.machineTimezone = setting.value!
                break;
        }
    }

    async function updateSetting(key: string, id: String, value: any) {
        try {
            await http.put('/settings/settings/' + id, {
                value: value,
                machineID: ctx.machineGUID
            })

            // in-case we need special function for specific keys
            switch (key) {
                case 'system_language':
                    ctx.setLocale(value)
                    window.location.reload();
                    break;
            }

            await loadSettings(true);

            const result = settings.value.filter(s => s.id == id)
            if (result.length) {
                result[0].value = value;
                legacyLoad(result[0]);
            }
        } catch (e) {
            console.log(e);
            await loadSettings(true);
        }
    }

    function getValue<T>(key: string): any {
        for (const item of settings.value) {
            if (item.key == key) {
                return item.value;
            }
        }
        return null
    }

    return {
        sections,
        settings,
        loadSettings,
        loadStoragePath,
        updateSetting,
        getValue,
        storagePath,
        baseUrl,
        showPrivateSettings,
        cities
    }
});
